import { useState, useEffect, useRef } from 'react';
import { SERVER_URL, fetchMyProfile } from '../services/postService';
import { getUser, isLoggedIn } from '../services/authService';

function AreaSummary({ selectedLang, onTranslate }) {
  const [userCity, setUserCity] = useState('');
  const [activeArea, setActiveArea] = useState('');   // '' means All India
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);

  const requestRef = useRef(0);            // ignore answers from older requests
  const userSearchedRef = useRef(false);   // true once the user picks a place themselves

  // ---------- load a summary ----------
  const loadSummary = async (area) => {
    const id = ++requestRef.current;
    setActiveArea(area);
    setLoading(true);
    setError('');

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 40000); // never "stuck" forever

    try {
      const res = await fetch(`${SERVER_URL}/api/summary?area=${encodeURIComponent(area)}`, {
        signal: controller.signal,
      });
      const json = await res.json();
      if (id !== requestRef.current) return;
      if (!res.ok) throw new Error(json.error || 'Could not load the summary');
      setData(json);
    } catch (e) {
      if (id !== requestRef.current) return;
      setError(
        e.name === 'AbortError'
          ? 'This is taking too long. Tap Refresh to try again.'
          : e.message || 'Could not load the summary. Is the backend running?'
      );
    } finally {
      clearTimeout(timer);
      if (id === requestRef.current) setLoading(false);
    }
  };

  // ---------- first load: the user's registered city, else All India ----------
  useEffect(() => {
    const init = async () => {
      const localCity = isLoggedIn() ? (getUser()?.city || '').trim() : '';
      setUserCity(localCity);
      loadSummary(localCity);

      // the database holds the real saved city (login does not return it)
      if (isLoggedIn()) {
        try {
          const me = await fetchMyProfile();
          const saved = me && me.city ? me.city.trim() : '';
          if (saved && saved !== localCity) {
            localStorage.setItem('locallens_user', JSON.stringify({ ...getUser(), city: saved }));
            setUserCity(saved);
            if (!userSearchedRef.current) loadSummary(saved);
          }
        } catch (e) {
          // offline: keep what we have
        }
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- place suggestions (free OpenStreetMap), after the user stops typing ----------
  useEffect(() => {
    if (!showSuggestions || query.trim().length < 3) {
      setSuggestions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query.trim())}&countrycodes=in&format=json&limit=5`
        );
        setSuggestions(await res.json());
      } catch (e) {
        setSuggestions([]);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [query, showSuggestions]);

  const search = (term) => {
    const t = (term !== undefined ? term : query).trim();
    if (!t) return;
    userSearchedRef.current = true;
    setShowSuggestions(false);
    setSuggestions([]);
    setQuery(t);
    loadSummary(t);
  };

  const pickChip = (area) => {
    userSearchedRef.current = true;
    setQuery('');
    setShowSuggestions(false);
    setSuggestions([]);
    loadSummary(area);
  };

  // ---------- read aloud ----------
  const handleListen = async () => {
    if (!data || !data.summary) return;
    window.speechSynthesis.cancel();

    let text = data.summary;
    if (selectedLang && selectedLang.code !== 'en') {
      try {
        const res = await fetch(
          `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text.slice(0, 450))}&langpair=en|${selectedLang.code}`
        );
        const json = await res.json();
        text = json.responseData.translatedText || text;
      } catch (e) {
        // speak English if translation fails
      }
    }

    const utterance = new SpeechSynthesisUtterance(text);
    const code = selectedLang ? selectedLang.code : 'en';
    const voice = window.speechSynthesis.getVoices().find((v) => v.lang.startsWith(code));
    if (voice) utterance.voice = voice;
    utterance.lang = code + '-IN';
    utterance.rate = 0.9;
    utterance.onend = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const isAllIndia = activeArea === '';
  const isMyCity = userCity !== '' && activeArea.toLowerCase() === userCity.toLowerCase();

  return (
    <section id="ai-summary-section" className="w-full px-6 lg:px-16 pb-20">
      <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-[40px] p-8 md:p-12 backdrop-blur-xl shadow-2xl max-w-5xl mx-auto">

        <div className="inline-flex items-center gap-2 bg-purple-500/20 px-4 py-2 rounded-full text-sm text-purple-300 mb-4">
          {data && data.count > 0 && data.usedAi === false ? '📊 FACTS FROM REPORTS' : '🤖 AI GENERATED SUMMARY'}
        </div>

        <h2 className="text-3xl md:text-4xl font-black leading-tight mb-2">
          Area Summary
          <span className="bg-gradient-to-r from-cyan-400 to-purple-400 text-transparent bg-clip-text">
            {' '}· {data ? data.area : isAllIndia ? 'All India' : activeArea}
          </span>
        </h2>

        {data && !loading && (
          <p className="text-gray-400 text-sm mb-4">
            Based on {data.count} report{data.count === 1 ? '' : 's'} · {data.window}
          </p>
        )}

        {/* WHICH AREA? */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {userCity && (
            <button
              onClick={() => pickChip(userCity)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                isMyCity ? 'bg-cyan-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              📍 {userCity}
            </button>
          )}
          <button
            onClick={() => pickChip('')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
              isAllIndia ? 'bg-cyan-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            🇮🇳 All India
          </button>
        </div>

        {/* SEARCH ANY PLACE */}
        <div className="relative mb-6">
          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
              placeholder="Search a place — e.g. Hyderabad, Nalgonda, Kukatpally..."
              className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition"
            />
            <button
              onClick={() => search()}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3 rounded-2xl font-bold hover:scale-105 transition"
            >
              🔍 Summarise
            </button>
          </div>

          {suggestions.length > 0 && (
            <div className="absolute z-30 w-full mt-2 bg-[#0f172a] border border-white/10 rounded-2xl overflow-hidden shadow-2xl max-h-64 overflow-y-auto">
              {suggestions.map((place, i) => (
                <button
                  key={i}
                  onClick={() => search(place.display_name.split(',')[0])}
                  className="w-full text-left px-4 py-3 hover:bg-white/10 transition border-b border-white/5 last:border-0"
                >
                  <p className="text-white text-sm font-semibold">📍 {place.display_name.split(',')[0]}</p>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {place.display_name.split(',').slice(1, 4).join(',')}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* THE SUMMARY */}
        <div className="min-h-[72px]">
          {loading ? (
            <p className="text-gray-300 text-lg animate-pulse">
              🤖 Reading reports from {isAllIndia ? 'All India' : activeArea}...
            </p>
          ) : error ? (
            <p className="text-red-300 text-lg">⚠️ {error}</p>
          ) : (
            data && <p className="text-gray-300 text-lg leading-relaxed">{data.summary}</p>
          )}
        </div>

        {/* ACTIONS */}
        <div className="flex flex-wrap gap-3 pt-4 mt-4 border-t border-white/10">
          {!isSpeaking ? (
            <button
              onClick={handleListen}
              disabled={loading || !data}
              className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-2.5 rounded-full font-semibold text-sm hover:scale-105 transition disabled:opacity-40"
            >
              🔊 Listen to this summary
            </button>
          ) : (
            <button
              onClick={handleStop}
              className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 px-5 py-2.5 rounded-full font-semibold text-sm hover:scale-105 transition animate-pulse"
            >
              ⏹️ Stop speaking
            </button>
          )}
          <button
            onClick={() => loadSummary(activeArea)}
            className="flex items-center gap-2 bg-white/10 border border-white/10 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-white/20 transition"
          >
            🔄 Refresh
          </button>
          <button
            onClick={onTranslate}
            className="flex items-center gap-2 bg-white/10 border border-white/10 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-white/20 transition"
          >
            🌐 Read in another language
          </button>
        </div>
      </div>
    </section>
  );
}

export default AreaSummary;